import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, CheckCircle, XCircle, Eye, EyeOff, Loader2, Save, Lock, UserCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      await authService.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
      });
      
      await refreshUser();
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsSaving(true);

    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsChangingPassword(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to change password. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <SEO
        title="Profile - Book My Yoga TTC"
        description="View and edit your profile information."
        url="https://bookmyyogattc.com/profile"
      />
      <div className="pt-20 min-h-screen flex">
        {/* Left Side - Visual */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange via-orange-dark to-orange relative overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="/images/cover.png" 
              alt="Yoga" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-orange/90 via-orange-dark/80 to-orange/90"></div>
          </div>
          
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="profilePattern" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="white" strokeWidth="1"/>
                  <circle cx="60" cy="60" r="35" fill="none" stroke="white" strokeWidth="0.5"/>
                  <circle cx="60" cy="60" r="20" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#profilePattern)" />
            </svg>
          </div>
          
          <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-md"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="text-5 mb-6"
              >
                👤
              </motion.div>
              <h2 className="text-5 font-semibold mb-4">Your Profile</h2>
              <p className="text-4 opacity-95 leading-relaxed">
                Manage your account information and preferences. Keep your details up to date to ensure the best experience.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Right Side - Content */}
        <div className="w-full lg:w-1/2 flex items-start justify-center bg-white p-6 lg:p-12 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-2xl space-y-6"
          >
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-5 font-semibold text-ivory-900 mb-2">Profile Settings</h1>
              <p className="text-ivory-600">Manage your account information and security</p>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-2 flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-2 flex items-center space-x-2">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Account Status */}
            <div className="bg-ivory-50 rounded-xl p-6 border border-ivory-200">
              <h3 className="text-4 font-semibold text-ivory-900 mb-4 flex items-center">
                <UserCircle className="h-5 w-5 mr-2 text-primary" />
                Account Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-ivory-700">Email Verified</span>
                  <div className="flex items-center space-x-2">
                    {user.emailVerified ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-green-600 font-medium">Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-600" />
                        <span className="text-red-600 font-medium">Not Verified</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ivory-700">Account Status</span>
                  <div className="flex items-center space-x-2">
                    {user.isActive ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-green-600 font-medium">Active</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-600" />
                        <span className="text-red-600 font-medium">Inactive</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ivory-700">Member Since</span>
                  <span className="text-ivory-900 font-medium">
                    {user.createdAt && !isNaN(new Date(user.createdAt).getTime())
                      ? new Date(user.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Profile Information */}
            <div className="bg-white rounded-xl shadow-xl p-8 border border-ivory-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-4 font-semibold text-ivory-900 flex items-center">
                  <User className="h-5 w-5 mr-2 text-primary" />
                  Personal Information
                </h3>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-2 font-semibold text-primary hover:text-primary-dark transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleSaveProfile} className="space-y-5">
                  <div>
                    <label htmlFor="firstName" className="block text-ivory-700 font-medium mb-2">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-ivory-700 font-medium mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="Enter your last name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-ivory-700 font-medium mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={user.email}
                      disabled
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg bg-ivory-50 text-ivory-500 cursor-not-allowed"
                    />
                    <p className="mt-1 text-2 text-ivory-500">Email cannot be changed</p>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-ivory-700 font-medium mb-2 flex items-center">
                      <Phone className="h-4 w-4 mr-2" />
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="flex-1 flex justify-center items-center py-3 px-6 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          firstName: user.firstName || '',
                          lastName: user.lastName || '',
                          phone: user.phone || '',
                        });
                        setError('');
                      }}
                      className="px-6 py-3 border border-ivory-300 text-ivory-700 font-semibold rounded-lg hover:bg-ivory-50 focus:outline-none focus:ring-2 focus:ring-ivory-300 focus:ring-offset-2 transition-colors duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-2 text-ivory-500 mb-1">First Name</label>
                    <p className="text-ivory-900 font-medium">{user.firstName}</p>
                  </div>
                  <div>
                    <label className="block text-2 text-ivory-500 mb-1">Last Name</label>
                    <p className="text-ivory-900 font-medium">{user.lastName}</p>
                  </div>
                  <div>
                    <label className="block text-2 text-ivory-500 mb-1 flex items-center">
                      <Mail className="h-4 w-4 mr-1" />
                      Email Address
                    </label>
                    <p className="text-ivory-900 font-medium">{user.email}</p>
                  </div>
                  <div>
                    <label className="block text-2 text-ivory-500 mb-1 flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      Phone Number
                    </label>
                    <p className="text-ivory-900 font-medium">{user.phone || 'Not provided'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Change Password */}
            <div className="bg-white rounded-xl shadow-xl p-8 border border-ivory-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-4 font-semibold text-ivory-900 flex items-center">
                  <Lock className="h-5 w-5 mr-2 text-primary" />
                  Security
                </h3>
                {!isChangingPassword && (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="text-2 font-semibold text-primary hover:text-primary-dark transition-colors"
                  >
                    Change Password
                  </button>
                )}
              </div>

              {isChangingPassword ? (
                <form onSubmit={handleChangePassword} className="space-y-5">
                  <div>
                    <label htmlFor="currentPassword" className="block text-ivory-700 font-medium mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        required
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className="w-full pl-4 pr-12 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ivory-400 hover:text-ivory-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-ivory-700 font-medium mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        required
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className="w-full pl-4 pr-12 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ivory-400 hover:text-ivory-600"
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-ivory-700 font-medium mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="w-full pl-4 pr-12 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ivory-400 hover:text-ivory-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="flex-1 flex justify-center items-center py-3 px-6 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Changing...
                        </>
                      ) : (
                        <>
                          <Lock className="h-5 w-5 mr-2" />
                          Change Password
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: '',
                        });
                        setError('');
                      }}
                      className="px-6 py-3 border border-ivory-300 text-ivory-700 font-semibold rounded-lg hover:bg-ivory-50 focus:outline-none focus:ring-2 focus:ring-ivory-300 focus:ring-offset-2 transition-colors duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-ivory-600">Click "Change Password" to update your password</p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Profile;

